import React, { useCallback, useEffect } from 'react';
import { getFieldProps, Page, Form, Heading, Inputs, Label, GridLayout } from '@crx-dev/hss-react-components';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { makeRequest } from '@crx-dev/hss-react-infrastructure';
import { useMutation, useQueryClient } from 'react-query';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useGetReturnUrl } from '../../../../Infrastructure/pageState/useGetReturnUrl';
import { RegisterSummary } from '../../Registers/types/RegisterSummary';
import { useUserInfo } from '../../../../Infrastructure/auth/UseUserInfo';
import { titleCase } from 'title-case';
import { scrollTo } from '../../../Common/Helpers/PageScrollingHelper';
interface SuppliedFormProps extends RouteComponentProps<{ registerId?: string }> {
    registerSummary: RegisterSummary;
}

function SuppliedForm({ match, registerSummary }: SuppliedFormProps) {
    const queryClient = useQueryClient();
    const returnUrl = useGetReturnUrl();
    const registerId = match.params.registerId;
    const history = useHistory();
    const userInfo = useUserInfo();

    const mutation = useMutation<unknown, unknown, any>(item =>
        makeRequest({
            method: 'POST',
            path: `/api/v1/controldrugregister/${registerId}/supplied`,
            data: { ...item, user: userInfo.name }
        })
    );

    const formik = useFormik({
        initialValues: {
            dateSupplied: new Date(Date.now()),
            quantity: 0,
            deliveredOrCollected: '',
            patient: {
                name: '',
                address: ''
            },
            prescriber: {
                name: '',
                address: '',
                referenceNumber: ''
            },
            collector: {
                name: '',
                address: '',
                idChecked: false,
                idRequested: false
            },
            deliveredBy: '',
            checkedBy: '',
            balance: registerSummary.balance,
            entryMadeBy: userInfo.name
        },
        validationSchema: yup.object().shape({
            dateSupplied: yup.date().required('Required'),
            quantity: yup
                .number()
                .moreThan(0)
                .required('Required')
                .max(
                    registerSummary.balance,
                    `The quantity supplied cannot be greater than the total balance (${registerSummary.balance})`
                ),
            patient: yup.object().shape({
                name: yup.string().required('Required'),
                address: yup.string().required('Required')
            }),
            prescriber: yup.object().shape({
                name: yup.string().required('Required'),
                address: yup.string(),
                referenceNumber: yup.string().required('Required')
            }),
            deliveredOrCollected: yup.string().required('Required'),
            collector: yup
                .object()
                .when('deliveredOrCollected', {
                    is: (deliveredOrCollected: string) => deliveredOrCollected === 'collected',
                    then: yup.object().shape({
                        name: yup.string().required('Required'),
                        address: yup.string(),
                        idChecked: yup.bool().required('Required'),
                        idRequested: yup.bool().required('Required')
                    }),
                    otherwise: yup.object()
                })
                .when('deliveredOrCollected', {
                    is: (deliveredOrCollected: string) => deliveredOrCollected === 'collectedByPatient',
                    then: yup.object().shape({
                        name: yup.string().required('Required'),
                        address: yup.string().required('Required'),
                        idChecked: yup.bool().required('Required')
                    }),
                    otherwise: yup.object()
                }),
            deliveredBy: yup.string().when('deliveredOrCollected', {
                is: (deliveredOrCollected: string) => deliveredOrCollected === 'delivered',
                then: yup.string().required('Required'),
                otherwise: yup.string()
            }),
            checkedBy: yup.string().required('Required')
        }),
        onSubmit: async values => {
            applyTitleCaseFormat();
            await mutation.mutateAsync(values);
            await queryClient.invalidateQueries(['register-summary', registerId]);
            history.push(returnUrl);
        }
    });

    useEffect(() => {
        formik.setFieldValue('balance', registerSummary.balance - Number(formik.values.quantity));
    }, [formik.values.quantity]);

    useEffect(() => {
        if (formik.values.deliveredOrCollected === 'collectedByPatient') {
            formik.setFieldValue('collector.name', formik.values.patient.name);
            formik.setFieldValue('collector.address', formik.values.patient.address);
        } else {
            formik.setFieldValue('collector.name', '');
            formik.setFieldValue('collector.address', '');
        }
    }, [formik.values.deliveredOrCollected]);

    const handleCancelClick = useCallback(() => {
        history.push(returnUrl);
    }, []);

    const applyTitleCaseFormat = () => {
        formik.values.patient.name = titleCase(formik.values.patient.name);
        formik.values.patient.address = titleCase(formik.values.patient.address);

        formik.values.entryMadeBy = titleCase(formik.values.entryMadeBy);
        formik.values.checkedBy = titleCase(formik.values.checkedBy);

        formik.values.prescriber.name = titleCase(formik.values.prescriber.name);
        formik.values.prescriber.address = titleCase(formik.values.prescriber.address);

        if (formik.values.deliveredOrCollected === 'delivered') {
            formik.values.deliveredBy = titleCase(formik.values.deliveredBy);
        } else {
            formik.values.collector.name = titleCase(formik.values.collector.name);
            formik.values.collector.address = titleCase(formik.values.collector.address);
        }
    };

    return (
        <Page>
            <Page.Header
                title="Supplied Entry"
                subtitle={registerSummary.productName}
                onBackClick={() => history.push(returnUrl)}
            />
            <Page.Content>
                <Form onSubmit={formik.handleSubmit}>
                    <Form.Section>
                        <Heading>Entry Details</Heading>
                        <Form.Columns columnWidths="calc(50% - 12px) calc(50% - 12px)">
                            <>
                                <Label label="Date" htmlFor="dateSupplied" />
                                <Inputs.DatePicker disabled {...getFieldProps(formik, 'dateSupplied')} />
                            </>
                        </Form.Columns>
                    </Form.Section>
                    <Form.Section>
                        <Heading>Patient Details</Heading>
                        <Label label="Patient Name" htmlFor="patient.name" />
                        <Inputs.Text {...getFieldProps(formik, 'patient.name')} />
                        <Label label="Patient Address" htmlFor="patient.address" />
                        <Inputs.Text {...getFieldProps(formik, 'patient.address')} onBlur={() => scrollTo(190)} />
                    </Form.Section>
                    <Form.Section key="supplyDetails">
                        <Heading>Supply Details</Heading>
                        <GridLayout marginBottom={25}>
                            <Form.Columns columnWidths="calc(50% - 12px) calc(50% - 12px)">
                                <>
                                    <Label label="Quantity" htmlFor="quantity" />
                                    <Inputs.Number {...getFieldProps(formik, 'quantity')} decimal />
                                </>
                            </Form.Columns>
                        </GridLayout>
                        <Form.Columns columnWidths="calc(50% - 12px) calc(50% - 12px)">
                            <>
                                <Label label="Entry Made By" htmlFor="entryMadeBy" />
                                <Inputs.Text {...getFieldProps(formik, 'entryMadeBy')} />
                            </>
                            <>
                                <Label label="Entry Booked In By" htmlFor="checkedBy" />
                                <Inputs.Text {...getFieldProps(formik, 'checkedBy')} />
                            </>
                        </Form.Columns>
                    </Form.Section>
                    <Form.Section>
                        <Heading>Prescriber Details</Heading>
                        <Label label="Name" htmlFor="prescriber.name" />
                        <Inputs.Text {...getFieldProps(formik, 'prescriber.name')} />
                        <Label label="Address" htmlFor="prescriber.address" />
                        <Inputs.Text {...getFieldProps(formik, 'prescriber.address')} />
                        <Form.Columns columnWidths="calc(50% - 12px) calc(50% - 12px)">
                            <>
                                <Label label="Ref No:" htmlFor="field1" />
                                <Inputs.Text
                                    {...getFieldProps(formik, 'prescriber.referenceNumber')}
                                    onBlur={() => scrollTo(-1)}
                                />
                            </>
                        </Form.Columns>
                    </Form.Section>
                    <Form.Section>
                        <Heading>Collection Details</Heading>
                        <Label label="Method of Collection" htmlFor="myField" />
                        <Inputs.RadioButtons
                            {...getFieldProps(formik, 'deliveredOrCollected')}
                            items={[
                                { value: 'collectedByPatient', label: 'Collected by patient' },
                                { value: 'collected', label: 'Collected by representative' },
                                { value: 'delivered', label: 'Delivered' }
                            ]}
                        />

                        {(formik.values.deliveredOrCollected === 'collected' ||
                            formik.values.deliveredOrCollected === 'collectedByPatient') && (
                            <>
                                <>
                                    <Label label="Name" htmlFor="collector.name" />
                                    <Inputs.Text {...getFieldProps(formik, 'collector.name')} />
                                </>
                                <>
                                    <Label label="Address" htmlFor="collector.address" />
                                    <Inputs.Text {...getFieldProps(formik, 'collector.address')} />
                                </>
                                <Label
                                    label={`Was the ${
                                        formik.values.deliveredOrCollected === 'collected'
                                            ? `representative's`
                                            : `patient's`
                                    } ID Requested?`}
                                    htmlFor="collector.idRequested"
                                />
                                <Inputs.RadioButtons
                                    columns
                                    items={[
                                        {
                                            label: 'Yes',
                                            value: true
                                        },
                                        {
                                            label: 'No',
                                            value: false
                                        }
                                    ]}
                                    {...getFieldProps(formik, 'collector.idRequested')}
                                />
                                <Label
                                    label={`Has the ${
                                        formik.values.deliveredOrCollected === 'collected'
                                            ? `representative's`
                                            : `patient's`
                                    } ID been checked?`}
                                    htmlFor="collector.idChecked"
                                />
                                <Inputs.RadioButtons
                                    columns
                                    items={[
                                        {
                                            label: 'Yes',
                                            value: true
                                        },
                                        {
                                            label: 'No',
                                            value: false
                                        }
                                    ]}
                                    {...getFieldProps(formik, 'collector.idChecked')}
                                />
                            </>
                        )}

                        {formik.values.deliveredOrCollected === 'delivered' && (
                            <>
                                <Label label="Driver Name" htmlFor="deliveredBy" />
                                <Inputs.Text {...getFieldProps(formik, 'deliveredBy')} />
                            </>
                        )}
                    </Form.Section>
                    <Form.Section>
                        <Heading>Balance Details</Heading>
                        <Form.Columns columnWidths="calc(50% - 12px) calc(50% - 12px)">
                            <>
                                <Label label="Balance Preview" htmlFor="balance" />

                                <Inputs.Number disabled {...getFieldProps(formik, 'balance')} decimal />
                            </>
                        </Form.Columns>
                    </Form.Section>
                    <Form.Footer
                        onCancel={handleCancelClick}
                        isSubmitting={mutation.isLoading}
                        submitError={mutation.isError}
                        submitButtonLabel="CONFIRM ENTRY"
                    />
                </Form>
            </Page.Content>
        </Page>
    );
}

export default SuppliedForm;
